/**
 * Design tokens: the single source of truth for every value in `shared/ui`.
 *
 * THESE ARE TODAY'S COLOURS, not a new palette. The brief is a centralised, lightly
 * policed UI that looks almost the same, built so it can be changed later. So the values
 * here were read out of `web/css/main.css` and `store/rootComponents/Theme.js` as they
 * render now. Adopting a token changes nothing on screen; it just means there is one
 * place to change it next time.
 *
 * `main.css` currently holds 35 distinct colours across 128 usages: 19 neutrals and 16
 * colours, including five reds and two yellows. Most of that is accident rather than
 * intent. `DUPLICATES` below records which ones collapse into which, so the clean-up can
 * be applied as a reviewed change rather than quietly folded in here.
 *
 * Deliberately plain data with no MUI import, so it outlives MUI. If the component layer
 * is ever reimplemented on something else, this file does not change.
 */

/** What renders today. Names describe the job, not the swatch. */
export const color = {
  // Brand. #345784 is the live theme's `bootstrap.blue` and appears 16 times in main.css.
  brand: '#345784',
  brandFocus: '#4e7298', // dropdown-toggle focus background
  brandLink: '#337ab7', // Bootstrap's own anchor blue, still used in two places
  chrome: '#2c2e3a', // sidebar

  // Neutrals, darkest to lightest. Repointed to the 3a handoff in SURF-4808 step 2;
  // see the note at the top of css/tokens.css for what moved and what did not.
  black: '#000000',
  ink: '#262626',
  ink2: '#333333',
  muted: '#555555',
  subtle: '#999999',
  border: '#c6ced0',
  borderLight: '#dddddd',
  fill: '#f5f5f5',
  offWhite: '#fafafa',
  /* Row tracking, shared with tokens.css and used by the MUI table and data grid so
     all three renderers stripe, band and hover identically. */
  tableStripeBg: '#fafbfc',
  rowHoverBg: '#eef0f2',
  tableHeadBg: '#f2f3f5',
  /** A selected row: brand at about 8%, the MUI tables' and the data grid's copy. */
  rowSelectedBg: '#eef3f9',
  // Two steps the ramp did not have until the table needed them. CSS-only until the MUI
  // theme had to paint a table cell the same colour main.css does.
  ink3: '#444444',
  line: '#e6e6e6',
  white: '#ffffff',

  /**
   * MUI's default primary blue. NOT the CMS brand — it is what MUI ships, and it has
   * leaked into 14 files as an accent. Whether those should become `brand` is a design
   * decision, not a clean-up, so it keeps its own name for now.
   *
   * It was written three ways: #1976D2 (MUI's actual value), #1A76D2 and #1B76D2. The
   * latter two are one-character typos, one and two off in the red channel, and are
   * indistinguishable on screen.
   */
  accent: '#1976D2',

  /**
   * Neutral ramp, from the greys actually in use in React. 24 distinct values collapsed
   * to these nine: every absorbed value was within about 8 luminance points of its
   * neighbour and indistinguishable on screen. Four greys sat between luminance 144 and
   * 148 alone (#909090, #939090, #92949c, #949494).
   *
   * Named by lightness rather than by job, because these are used for borders, text and
   * backgrounds interchangeably and inventing semantics for them would be a guess.
   *
   * Not yet unified with the neutrals above, which came from main.css. Doing that means
   * changing rendered colours in the stylesheet, which is the held CSS half of step 2.
   */
  neutral900: '#212529',
  neutral800: '#484646',
  neutral700: '#696969',
  neutral600: '#747474',
  neutral500: '#888888',
  neutral400: '#949494',
  neutral300: '#cccccc',
  neutral200: '#e0e0e0',
  neutral100: '#eeeeee',

  /**
   * The MUI blue family, as the info panels actually use it. `accent` is blue-700;
   * these are the surrounding steps of the same palette, and they are separate tokens
   * rather than one because the trio is a background, its border and its text, so folding
   * any two together would cost contrast.
   */
  accentSurface: '#E3F2FD', // blue-50: the panel background
  accentBorder: '#90CAF9', // blue-200: its border
  accentDark: '#1565C0', // blue-800: text on it, and the icon hover
  accentDisabled: '#93B2C2', // the disabled state of an accent icon button

  /**
   * `brand` under the pointer. Not a duplicate of `brand`: it is the hover of a button
   * whose resting colour *is* `brand`, so collapsing the two would remove the hover.
   */
  brandHover: '#2A466A',

  /**
   * The two `react-switch` tracks. The library takes a colour rather than a class, so
   * these cannot live in CSS. `switchTrack` pairs with `accent` on the handle and
   * `switchTrackAlt` with `brand`.
   */
  switchTrack: '#8CBAE8',
  switchTrackAlt: '#ACC0D0',

  /** SurveyJS property-grid action button, in both the forms and quizzes wrappers. */
  surveyAccent: '#113483',

  /** Half-transparent black and white, for overlays and disabled glyphs. */
  ink50: '#00000080',
  white50: '#FFFFFF80',

  /** Bootstrap's status colours, used for inline status text in a handful of screens. */
  errorText: '#dc3545',
  successText: '#28a745',

  // Status. Taken from the live MUI theme, which is the authority for these.
  success: '#155724',
  successDot: '#50c878',
  warning: '#ffd90b',
  /** Pending / in-progress. See the note in tokens.css for why there are two. */
  pending: '#FFC107',
  pendingText: '#DBA800',

  /** Translucent fills. Alpha, so they cannot collapse onto a solid neutral. */
  hoverFill: '#0000000a',
  rowStripe: '#f2f2f2',
  ink60: '#00000099',
  accent70: '#1976d2b3',
  ruleFaint: '#d6d6d680',
  /**
   * Alpha variants that exist because Tailwind's `/NN` opacity modifier does not work
   * on a colour declared as `var()`. It compiles to nothing at all rather than failing,
   * so every translucent value has to be its own token.
   */
  ringBrand: '#4e72984d',
  ringDanger: '#a944424d',
  dangerTint: '#a944421a',

  rating: '#ffd700',
  ink87: '#000000de',
  danger: '#a94442',
  dangerStrong: '#d22b2b',
};

/**
 * Colours that WERE duplicates and have been collapsed onto the token named here.
 * Applied 2026-09-22 across `main.css`, `simple-sidebar.css` and the React tree.
 *
 * Kept as a record rather than deleted: if one of these hex values reappears, this is the
 * evidence that removing it was deliberate, and `.harness/collapseLegacyCss.js` shows the
 * perceptual delta that justified each one.
 */
/**
 * Table geometry, shared with `css/tokens.css`.
 *
 * The CMS renders tables three ways: Bootstrap `.table`, MUI's `Table` (and
 * `mui-datatables` on top of it), and `@mui/x-data-grid`. main.css styles the first from
 * the CSS custom properties; the MUI theme styles the other two from these. They are the
 * same numbers, and `tokensCss.test.js` fails if they drift.
 */
export const table = {
  headHeight: 36,
  rowHeight: 52,
  rowHeightStacked: 60,
  cellPadX: 16,
  /** A row's vertical inset; a row grows past `rowHeight` when its title wraps. */
  cellPadY: 8,
  /** The title column, and how narrow it may get before a wide table scrolls sideways. */
  colPrimary: 392,
  colPrimaryMin: 280,
  pagerHeight: 56,
};

export const COLLAPSED = {
  // Typos of MUI's default blue, found across 14 React files.
  '#1a76d2': 'accent',
  '#1b76d2': 'accent',
  // A one-character typo of the brand colour, on `.prev-link` (main.css:550).
  '#345785': 'brand',
  // Near-whites. #eeeeee is no longer here: it is a live token (`neutral100`) because
  // React uses it in its own right, so #ebebeb collapses onto that rather than onto
  // `fill`, which is seven luminance points further away.
  '#f9f9f9': 'offWhite',
  '#ebebeb': 'neutral100',
  // Light greys. #cccccc likewise became `neutral300`, so it is a target, not a duplicate.
  '#dee2e6': 'neutral200',
  // Two spellings of the same grey.
  '#9a9a9a': 'subtle',
  '#666666': 'muted',
  // Three of the five reds.
  '#bf4644': 'danger',
  // Two yellows.
  '#ffd300': 'warning',
  // One of the near-identical blues.
  '#255884': 'brand',
};

/**
 * Colours that were NOT collapsed, because folding them in would have been visible.
 *
 * Each is over CIE76 ΔE 12 from its nearest neighbour, which is the threshold
 * `.harness/collapseLegacyCss.js` refuses to cross. They were given their own name
 * instead, and `css/tokens.css` defines all five. `#5e6e82` is the clearest case: it is
 * the `.cms-banner-user` text colour and sits ΔE 27 from `chrome`, so collapsing it would
 * have visibly darkened the username.
 *
 * This map exists because an earlier version of `COLLAPSED` listed these as collapsed,
 * which was wrong and contradicted both the stylesheet and the test below it.
 */
export const RENAMED = {
  '#5e6e82': 'slate', // ΔE 27 from chrome
  '#6794cf': 'brand-glow', // ΔE 17 from brand-focus
  '#bbbbbb': 'placeholder', // ΔE 12 from border-light
  '#b8312f': 'danger-alt', // ΔE 17 from danger
  '#b20000': 'danger-dark', // ΔE 13 from danger-strong
};

/**
 * `border-color: #eeee` appears four times (main.css:1974, 1992, 2009, 2023). Four-digit
 * hex is valid CSS Color Level 4 — `#RGBA` — so this renders as #eeeeee at 93% alpha
 * rather than being ignored. It is almost certainly meant to be `#eee`. Fixing it is a
 * sub-1% alpha change nobody will see, but it should be deliberate.
 */
export const SUSPECT = {
  // Resolved 2026-09-22: collapsed onto `fill`. Left here as the record.
  '#eeee': 'was #eeeeee at 93% alpha (valid #RGBA); meant #eee; now --fill',
};

/**
 * Elevation, mirroring `--shadow-sm` / `--shadow-md` / `--shadow-lg` in tokens.css.
 *
 * The names differ from the CSS ones for historical reasons and are left alone rather
 * than renamed across 40-odd call sites; `xs` is `--shadow-sm`, `lg` is `--shadow-md`,
 * `xl` is `--shadow-lg`. The values have to stay in step with that file.
 *
 * Each step is a close contact shadow plus a wider ambient one. A single soft blur reads
 * as blur; the pair is what reads as depth.
 */
export const shadow = {
  xs: '0 1px 2px rgba(16, 24, 40, .08), 0 1px 3px rgba(16, 24, 40, .10)',
  lg: '0 12px 24px -4px rgba(16, 24, 40, .18), 0 4px 8px -2px rgba(16, 24, 40, .12)',
  xl: '0 24px 48px -12px rgba(16, 24, 40, .30), 0 8px 16px -6px rgba(16, 24, 40, .14)',
};

/**
 * Geometry, mirroring the custom properties in `css/tokens.css`.
 *
 * An earlier version of this object held 6/8/12/16, which was the radius scale from the
 * "CMS Soft" design handoff rather than anything the app renders. Nothing consumed it, so
 * nothing broke, but it meant the JS half of the token set quietly described a different
 * product from the CSS half. These values are now what actually renders, and
 * `__tests__/tokensCss.test.js` fails if the two drift.
 *
 * Numbers, not strings, because MUI's theme wants numbers and CSS does not care.
 */
/**
 * `react-switch` takes its shadows as props rather than classes, so they cannot be CSS.
 * Four switches carried the same pair by copy-paste; now they share one definition.
 */
export const switchShadow = {
  rest: '0px 1px 5px #00000099',
  active: '0px 0px 1px 10px #00000033',
};

export const radius = { chip: 6, control: 8, card: 12, dialog: 16, pill: 999, check: 4 };

/** The control rhythm: what a button, input and select are built out of. */
export const control = {
  padY: 8,
  padX: 14,
  textareaMinHeight: 80,
  /** --control-h-input: an input or select, two pixels taller than a button. */
  inputHeight: 40,
  /** --control-h-sm: pager pages, Previous and Next, a bulk action. */
  smallHeight: 32,
  /** --label-gap: a static label to its control. */
  labelGap: 6,
  switchWidth: 36,
  switchHeight: 20,
  switchKnob: 16,
  checkSize: 18,
  /** --chip-h: a selected value inside a multi-select. */
  chipHeight: 28,
};

/**
 * Colour jobs moved by redesign iteration 1 (2026-09-29), mirroring the aliases at the end
 * of `css/tokens.css`. No new colour: each points at an existing one, named for the element
 * that moved, so the MUI theme and the stylesheets move together.
 */
export const role = {
  inputBorder: color.ink50,
  switchOff: color.neutral500,
  placeholderInk: color.neutral600,
  captionInk: color.muted,
  helpIcon: color.muted,
  checkOn: color.brand,
  sectionTitleInk: color.ink,
  pageCurrent: color.brand,
};

/** Editor pages: the form column, its field grid, the section index and the action bar. */
export const form = {
  width: 880,
  gapX: 24,
  gapY: 20,
  sectionGap: 24,
  indexWidth: 208,
  indexGap: 40,
  actionBarHeight: 64,
};

/** The list filter band: five filters a row at 1440, the search a little wider. */
export const toolbar = { gap: 16, filterWidth: 212, searchWidth: 320 };

/** Dialog sizes, by the `size` prop. */
export const dialog = {
  width: 600,
  widthSm: 400,
  widthLg: 860,
  widthXl: 1100,
  /* Dialogs are anchored in the upper third rather than centred; `top` and `maxHeight`
     are a pair, leaving the same gap above and below. Mirrored in css/tokens.css. */
  top: '12vh',
  maxHeight: '76vh',
  fullWidth: '96vw',
  fullHeight: '92vh',
  /** The layer Bootstrap gave its modals; the layers around it are placed relative to it. */
  z: 1050,
};

/**
 * Spacing, keyed by the number of pixels it renders.
 *
 * Every value here is one that the app actually uses; nothing was rounded onto a scale,
 * because rounding would have moved things. `space[10]` reads as "ten pixels" and returns
 * the string an inline style wants, so a call site stays legible and a pivot re-maps this
 * object rather than hunting through 107 inline styles.
 *
 * It was an array of a nominal 4px scale before, which nothing consumed and which did not
 * describe what the screens do: 5, 7, 9, 15, 25, 27 and 30 are all in use.
 *
 * `rem` and `em` spacing is deliberately not here. Those already scale with the type
 * scale, so they follow a pivot on their own.
 */
export const space = {
  0: '0px',
  1: '1px',
  2: '2px',
  3: '3px',
  4: '4px',
  5: '5px',
  6: '6px',
  7: '7px',
  8: '8px',
  9: '9px',
  10: '10px',
  12: '12px',
  15: '15px',
  16: '16px',
  20: '20px',
  24: '24px',
  25: '25px',
  27: '27px',
  30: '30px',
  32: '32px',
  40: '40px',
  50: '50px',
  56: '56px',
};

/** The same steps as numbers, for MUI's theme and anything doing arithmetic. */
export const spaceNum = Object.fromEntries(
  Object.entries(space).map(([k, v]) => [k, Number.parseInt(v, 10)])
);

/**
 * The typeface stays Open Sans, self-hosted from `web/fonts` and declared in four
 * `@font-face` blocks at the top of main.css: Regular, Bold, Italic, BoldItalic.
 *
 * There is no 500 or 600 face, and that turns out not to matter. An earlier note here
 * claimed semibold got a synthesised smear; it does not. CSS font matching picks a real
 * face: for a desired 600 it searches weights at or above 600 first and lands on the Bold
 * file, and for 500 it falls back to Regular. Nineteen places ask for 500 or 600 (one in
 * main.css, eighteen in React, most of them MUI `sx`), and every one of them is already
 * rendering a real file.
 *
 * So shipping `OpenSans-SemiBold.ttf` would not fix a defect: it would *change* those
 * nineteen from Bold to Semibold. That is a restyle and a font-licensing decision, not
 * polish, which is why it is not done here.
 */
export const font = {
  /* Open Sans, and it is first for a reason. The stack led with 'Figtree', which this
     repo has no @font-face for, no file in web/fonts and no CDN link, so it never
     resolved and every screen fell through to `system-ui` — the OS font, not the one
     anybody chose. Open Sans is the typeface the CMS ships (four @font-face rules at
     the top of main.css) and the one the decision below records. */
  family: "'Open Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  /**
   * Six steps, plus `display` for the one dashboard stat glyph.
   *
   * It was eleven, which is not a scale: it was whatever the app happened to render,
   * discovered by tokenising 29 inline `fontSize` literals. Naming them made the drift
   * visible; this collapses it. Five sizes moved, none by more than 3px:
   *
   *   subtitle 20 -> section 18   three headings
   *   form     17 -> lead 16      the auth screens
   *   medium   15 -> body 14      one italic footnote
   *   fine     11 -> caption 12   one field label
   *   micro     9 -> caption 12   one search hint, which was unreadably small
   */
  size: {
    display: 45, // the dashboard stat glyph, deliberately outside the scale
    title: 24,
    section: 18,
    lead: 16,
    body: 14,
    small: 13,
    caption: 12,
  },
  weight: { regular: 400, bold: 700 },
  /**
   * Weights the code asks for that have no file of their own. Kept named so a future
   * decision to ship a semibold face has one place to look, not so callers use them.
   */
  weightWanted: { medium: 500, semibold: 600 },
};

export const density = { comfortable: 52, compact: 40 };

export default {
  color,
  role,
  shadow,
  switchShadow,
  spaceNum,
  radius,
  control,
  form,
  toolbar,
  dialog,
  table,
  space,
  font,
  density,
  COLLAPSED,
  RENAMED,
  SUSPECT,
};
